import { getSupabaseClient } from '@/config';

/** Translator, passed in: these messages are read on the connect form. */
export type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Google service-account validation (client side).
 *
 * The browser can't validate a service account directly: it would have to sign an
 * RS256 JWT with the private key (which must never ship to the browser) and call
 * Google's token/Ads endpoints with a developer token. This posts to the server
 * proxy /api/google-sa-validate, which does the full chain server-side. The private
 * key + developer token transit to our own origin over TLS and are never persisted
 * by the endpoint.
 */

export interface GoogleServiceAccountValidation {
  valid: boolean;
  accountName?: string;
  error?: string;
}

export interface GoogleServiceAccountParams {
  serviceAccountJson: string;
  impersonatedUser: string;
  developerToken: string;
  customerId: string;
  loginCustomerId?: string;
}


/** The caller's session, which the validate proxies require. */
const authHeader = async (): Promise<Record<string, string>> => {
  const { data } = await getSupabaseClient().auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function validateGoogleServiceAccount(
  params: GoogleServiceAccountParams,
  t: TFn,
): Promise<GoogleServiceAccountValidation> {
  try {
    const resp = await fetch('/api/google-sa-validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // The proxy is a signed-in-only route now: it takes a credential and
        // reports whether it works, which is not something to offer the world.
        ...(await authHeader()),
      },
      body: JSON.stringify({
        service_account_json: params.serviceAccountJson,
        impersonated_user: params.impersonatedUser,
        developer_token: params.developerToken,
        customer_id: params.customerId,
        login_customer_id: params.loginCustomerId,
      }),
    });
    const data = (await resp.json().catch(() => null)) as
      | (Partial<GoogleServiceAccountValidation> & { error?: string })
      | null;
    if (data && typeof data.valid === 'boolean') {
      return { valid: data.valid, accountName: data.accountName, error: data.error };
    }
    return { valid: false, error: data?.error ?? t('settings.connectFailed', { platform: 'Google Ads API' }) };
  } catch {
    return { valid: false, error: t('settings.connectFailed', { platform: 'Google Ads API' }) };
  }
}
